/**
 * Builders for `tracking_events` rows — the per-item lifecycle timeline that drives forward-stocking
 * inventory status. Ported from onward-app/utils/trackingEvents.js.
 *
 * Web-wide row conventions, all enforced by the DB: `event_source` is always 'MANUAL' (a CHECK allows
 * only SCAN | MANUAL | UNKNOWN, and web has no scanner), and `event_lat` / `event_lng` / `driver_id`
 * are always null (no location source, and the driver FK targets a route-scoped driver record rather
 * than the dispatcher clicking the button).
 */

const EVENT_SOURCE = 'MANUAL';

/** An item coming back from a consignee was collected there, not delivered or picked up at an origin. */
export const RETURN_PICKUP = 'RETURN_ITEM_PICKED_UP_FROM_CONSIGNEE';

/** Callers pass `returnEventType` only where a return item's direction of travel changes the meaning. */
export const resolveItemEventType = (item, eventType, returnEventType) =>
    returnEventType && item?.is_return ? returnEventType : eventType;

const toTrackingEvent = (item, { eventType, returnEventType, eventTime }) => ({
    order_id: item?.order_id ?? null,
    item_id: item?.item_id,
    event_type: resolveItemEventType(item, eventType, returnEventType),
    event_time: eventTime,
    event_source: EVENT_SOURCE,
    event_lat: null,
    event_lng: null,
    driver_id: null,
});

/** True when the item's already-loaded `tracking_events` include the given type. */
export const itemHasEvent = (item, eventType) =>
    (item?.tracking_events || []).some((event) => event?.event_type === eventType);

/**
 * One row per item. Items with no `item_id` are dropped — a null would violate the FK.
 *
 * `skipExisting` compares against the *resolved* type, so a return still owed its return event is not
 * skipped for carrying the forward one. It only guards a later re-submit: `tracking_events_uniq`
 * includes `event_time`, so `on_conflict` alone would let a second click insert at a new timestamp.
 */
export const buildTrackingEvents = ({ items, eventType, returnEventType, eventTime, skipExisting = false } = {}) => {
    if (!eventType || !Array.isArray(items)) {
        return [];
    }

    const shared = {
        eventType,
        returnEventType,
        eventTime: eventTime || new Date().toISOString(),
    };

    return items
        .filter((item) => !!item?.item_id)
        .filter((item) => !skipExisting || !itemHasEvent(item, resolveItemEventType(item, eventType, returnEventType)))
        .map((item) => toTrackingEvent(item, shared));
};
